/**
 * Nepali and English for the interface.
 *
 * The message id *is* the Nepali string, rather than an invented key like
 * `reader.notFound`. Nepali is this platform's source language and it is already
 * complete, so a key layer would be pure bookkeeping — and anything missed keeps
 * rendering correct Nepali instead of leaking `reader.notFound` onto the page.
 * `scripts/i18n-scan.mjs` reports which strings in the source have no entry here.
 *
 * The *content* is never touched. A कविता is a कविता in either language; only
 * the chrome around it changes.
 */

import {
  CATEGORY_META,
  FEED_SORT_LABELS,
  formMeta,
  type FeedSort,
  type LiteraryCategory,
} from '@shared/types';

const KEY = 'kaha:lang';

export const LANGUAGES = [
  { code: 'ne', label: 'नेपाली' },
  { code: 'en', label: 'English' },
] as const;

export type Lang = (typeof LANGUAGES)[number]['code'];

/**
 * English is the default interface language; a stored choice always wins.
 *
 * Not the browser's language, either way. `navigator.language` is English on
 * most devices in Nepal regardless of what their owner reads, so it is not
 * evidence of anything — a deliberate choice is stored, and nothing else
 * overrides it.
 *
 * The default is English because the audience arriving cold is now wider than
 * the literary one: somebody who searched "nepali typing" may not read Devanagari
 * at all, and the *content* is unaffected — a कविता stays a कविता in either
 * setting. Only the chrome moves.
 */
function load(): Lang {
  try {
    return localStorage.getItem(KEY) === 'ne' ? 'ne' : 'en';
  } catch {
    return 'en';
  }
}

let current: Lang = load();

/*
 * The document's own language, set before React renders anything.
 *
 * Not decoration: `lang` is what a screen reader reads the page's voice from,
 * what the browser picks font fallbacks and hyphenation rules with, and what the
 * `:root[lang]` selectors in index.css key off. index.html ships `lang="ne"`, so
 * an English reader would otherwise have their interface announced in Nepali.
 */
if (typeof document !== 'undefined') {
  document.documentElement.lang = current;
}

export function lang(): Lang {
  return current;
}

/**
 * Switches language and reloads.
 *
 * A reload rather than a re-render, deliberately. It makes `t()` a plain module
 * function callable from anywhere — module scope, event handlers, helpers that
 * are not components — instead of a hook that every call site must subscribe to,
 * and it removes the entire class of bugs where one corner of the page keeps the
 * old language because it did not re-render. Drafts autosave, so there is
 * nothing to lose. It also lets the document's own `lang` attribute be set
 * correctly from the start rather than patched afterwards.
 */
export function setLang(next: Lang): void {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* Private mode: the choice lasts for this page only. */
  }
  current = next;
  window.location.reload();
}

const DEVANAGARI_DIGITS = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];

/**
 * Numerals in the reader's script.
 *
 * Devanagari digits in Nepali, Latin in English. Mixing them — "पृष्ठ 7" — reads
 * as a bug to a Nepali reader and as gibberish to anyone else.
 */
export function n(value: number | string): string {
  const text = String(value);
  return current === 'ne' ? text.replace(/[0-9]/g, (d) => DEVANAGARI_DIGITS[Number(d)]) : text;
}

/** BCP-47 tag for Intl and for the document's lang attribute. */
export function locale(): string {
  return current === 'ne' ? 'ne-NP' : 'en';
}

/**
 * Translates, and fills `{name}` placeholders.
 *
 * An unknown string falls through as Nepali. That is the failure mode worth
 * having: a missed string is slightly out of place rather than broken.
 */
export function t(ne: string, vars?: Record<string, string | number>): string {
  const base = current === 'en' ? (EN[ne] ?? ne) : ne;
  if (!vars) return base;
  return base.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * Labels for the literary taxonomy.
 *
 * The forms already carry both names — `FORM_META.ghazal` is `{ np: 'गजल',
 * en: 'Ghazal' }` — so these pick a side rather than translate. The English
 * names are transliterations, not translations, and deliberately so: a गजल is a
 * ghazal, and calling it a "sonnet" would be a lie about the form.
 */
export function formLabel(form: string): string {
  const meta = formMeta(form);
  return current === 'en' ? meta.en : meta.np;
}

export function categoryLabel(category: LiteraryCategory): string {
  const meta = CATEGORY_META[category];
  return current === 'en' ? meta.en : meta.np;
}

export function sortLabel(sort: FeedSort): string {
  const meta = FEED_SORT_LABELS[sort];
  return current === 'en' ? meta.en : meta.np;
}

const EN: Record<string, string> = {
  // — Brand and shell —
  'कलम प्रेस': 'Kalam Press',
  'कलम प्रेस — नेपाली साहित्यको घर': 'Kalam Press — a home for Nepali literature',
  'नेपाली साहित्यको मासिक संकलन': 'A monthly gathering of Nepali literature',
  'कविता · कथा · निबन्ध': 'Poetry · Fiction · Essays',
  'कविता, कथा र निबन्धका लागि नेपाली लेखकहरूको प्रकाशन मञ्च।':
    'A publishing home for Nepali poets, storytellers and essayists.',
  'कलम प्रेसमा बनेको': 'Made with Kalam Press',
  'आफ्नो साइट सुरु गर्नुहोस्': 'Start your own site',
  'यो पृष्ठ भेटिएन': 'Page not found',
  'लिंक पुरानो वा गलत हुन सक्छ।': 'That link may be out of date or mistyped.',
  'गृहपृष्ठ': 'Home',
  'मुख्य पृष्ठ': 'Front page',
  'यो अंक': 'This issue',
  'यो अंक खाली छ।': 'This issue is empty.',
  'पहिलो रचना प्रकाशित हुन बाँकी छ।': 'The first piece is yet to be published.',

  // — Navigation —
  'सबै रचना': 'All pieces',
  'परिचय': 'About',
  'खाता': 'Account',
  'मेनु': 'Menu',
  'लेखन कक्ष': 'Writing room',
  'नयाँ रचना': 'New piece',
  'तथ्यांक': 'Statistics',
  'थिम': 'Theme',
  'डोमेन सेटिङ': 'Domain settings',
  'प्रशासन': 'Admin',
  'बाहिरिनुहोस्': 'Sign out',
  'लगइन': 'Sign in',
  'लगइन गर्नुहोस्': 'Sign in',
  'लेख्न सुरु गर्नुहोस्': 'Start writing',
  'दर्ता': 'Register',
  'दर्ता गर्नुहोस्': 'Create account',
  'पढ्ने रङ्ग बदल्नुहोस्': 'Change the reading colours',
  'भाषा': 'Language',

  // — Feed —
  'पूरा पढ्नुहोस् →': 'Read on →',
  'थप पढ्नुहोस्': 'Read more',
  'अरू रचनाहरू': 'More pieces',
  'थप रचना': 'More pieces',
  'ल्याउँदै…': 'Loading…',
  'खोज्नुहोस्…': 'Search…',
  'रचना खोज्नुहोस्': 'Search pieces',
  'अहिलेसम्म कुनै रचना छैन।': 'Nothing published yet.',
  'रचनाहरू ल्याउन सकिएन। पुनः प्रयास गर्नुहोस्।': 'Could not load pieces. Please try again.',
  'मिनेट': 'min',

  // — Reader —
  'पृष्ठ': 'Page',
  'सामग्री': 'Contents',
  'पढाइ': 'Reading',
  'अघिल्लो': 'Previous',
  'अर्को': 'Next',
  'रचना भेटिएन': 'Piece not found',
  'यो लिंक पुरानो वा गलत हुन सक्छ।': 'That link may be out of date or mistyped.',
  'फिडमा फर्किनुहोस्': 'Back to the feed',
  'तपाईं पृष्ठ {page} मा पुग्नुभएको थियो।': 'You had reached page {page}.',
  'त्यहीँबाट जारी राख्नुहोस्': 'Continue from there',
  'सुरुबाटै': 'Start over',
  'बाहिर निस्कनुहोस् (Esc)': 'Leave full screen (Esc)',
  'पूर्ण स्क्रिन (F)': 'Full screen (F)',
  'पूर्ण स्क्रिनबाट बाहिर': 'Leave full screen',
  'पूर्ण स्क्रिन': 'Full screen',
  'मन पराउन लगइन गर्नुहोस्': 'Sign in to react',
  'अक्षर सानो': 'Smaller type',
  'अक्षर ठूलो': 'Larger type',
  'हरफ बीचको दूरी': 'Line spacing',
  'स्तम्भको चौडाइ': 'Column width',
  'पृष्ठ {page}/{total}': 'Page {page}/{total}',
  'पृष्ठ {page} / {total}': 'Page {page} / {total}',

  // — Sharing —
  'साझा गर्नुहोस्': 'Share',
  'फेसबुक': 'Facebook',
  'भाइबर': 'Viber',
  'ह्वाट्सएप': 'WhatsApp',
  'एक्स': 'X',
  'टेलिग्राम': 'Telegram',
  'लिंक्डइन': 'LinkedIn',
  'इमेल': 'Email',
  'लिंक कपी गर्नुहोस्': 'Copy link',
  'लिंक कपी भयो': 'Link copied',
  'थप विकल्प…': 'More…',

  // — Editor —
  'यहाँ लेख्न सुरु गर्नुहोस्…': 'Start writing here…',
  'Enter — नयाँ हरफ · खाली हरफमा फेरि Enter वा Ctrl+Enter — नयाँ श्लोक':
    'Enter — new line · Enter again on a blank line, or Ctrl+Enter — new stanza',
  'Enter — नयाँ अनुच्छेद · Shift+Enter — हरफ': 'Enter — new paragraph · Shift+Enter — line break',
  'लिंक ठेगाना (URL)': 'Link address (URL)',
  // The stanza break is the paragraph gap now; the rule is a mark between the
  // *parts* of a long poem, which is what it always rendered as.
  'खण्ड विभाजक चिन्ह': 'Section mark',
  'विभाजक': 'Divider',
  // The alternatives picker. The row labels themselves (मूर्धन्य, दीर्घ…) are
  // not here — they are grammatical terms attached to the candidates, and carry
  // their own np/en pair the way the literary taxonomy does.
  'अरू रूप': 'Other readings',
  'छान्न १–{last}': '1–{last} to pick',
  'शब्द मिलेन? Ctrl+Space — अरू रूप': 'Wrong word? Ctrl+Space — other readings',
  'शब्द अर्कै आयो भने Ctrl+Space थिच्नुहोस् — अरू रूपहरू देखिन्छन्, वा त्यही शब्दमा क्लिक गर्नुहोस्। तपाईंले रोजेको सम्झिन्छ।':
    'If the wrong word comes out, press Ctrl+Space — or click the word — for the other readings. Your choice is remembered.',
  'बायाँ': 'Left',
  'बीचमा (सबै छान्न Ctrl+A)': 'Centre (Ctrl+A to select all)',
  'दायाँ': 'Right',
  'तस्बिर (टाँस्न वा तान्न पनि सकिन्छ)': 'Image (you can also paste or drag one in)',
  'तस्बिरका लागि पहिले शीर्षक राख्नुहोस्': 'Give the piece a title before adding images',
  'देवनागरी': 'Devanagari',
  'बोल्ड': 'Bold',
  'इटालिक': 'Italic',
  'उपशीर्षक': 'Subheading',
  'उद्धरण': 'Quote',
  'सूची': 'List',
  'क्रमबद्ध सूची': 'Numbered list',
  'लिंक': 'Link',
  'नयाँ पृष्ठ (Ctrl+Shift+Enter)': 'New page (Ctrl+Shift+Enter)',
  'पूर्ववत्': 'Undo',
  'पुनः': 'Redo',
  'विशेष चिन्ह': 'Special characters',
  'टाइपिङ सहायता': 'Typing help',
  'रोमन → देवनागरी': 'Roman → Devanagari',
  'ॐ': 'ॐ',
  'क्लिक गर्नुहोस्, कर्सर भएकै ठाउँमा थपिन्छ।': 'Click one and it is inserted at the cursor.',
  'रोमनबाट देवनागरी': 'Roman to Devanagari',
  'अंग्रेजी अक्षरमा टाइप गर्नुहोस्, स्पेस थिच्नासाथ देवनागरीमा बदलिन्छ।':
    'Type in Latin letters; press space and it becomes Devanagari.',
  'रोमनमा लेखे पनि हुन्छ — फोकस हट्नासाथ देवनागरीमा बदलिन्छ।':
    'You can type in Latin letters — it converts when you move on.',

  // — Writing room —
  'रचना ल्याउन सकिएन।': 'Could not load the piece.',
  'सुरक्षित गर्न सकिएन।': 'Could not save.',
  'प्रकाशित गर्न सकिएन।': 'Could not publish.',
  'पहिले शीर्षक राखेर सुरक्षित गर्नुहोस्, त्यसपछि कभर थप्न सकिन्छ।':
    'Save it with a title first, then you can add a cover.',
  'पहिलो हरफ लेख्नुहोस्…': 'Write the first line…',
  'यहाँबाट सुरु हुन्छ…': 'It begins here…',
  'सुरक्षित गर्दै…': 'Saving…',
  'सुरक्षित भयो': 'Saved',
  'सुरक्षित भएन': 'Not saved',
  'रचनाको नाम': 'Title of the piece',
  'वैकल्पिक': 'optional',
  'प्रेम, प्रकृति, समाज': 'love, nature, society',
  'कभर हटाउनुहोस्': 'Remove cover',
  'पूर्वावलोकन': 'Preview',
  'शीर्षक': 'Title',
  'विधा': 'Form',
  'बदल्नुहोस्': 'Change',
  'शीर्षकविहीन': 'Untitled',
  'लुकाउनुहोस्': 'Unpublish',
  'प्रकाशित गर्नुहोस्': 'Publish',
  'यहाँ अझै केही छैन।': 'Nothing here yet.',
  'पहिलो रचना लेख्नुहोस्': 'Write your first piece',
  'हेर्नुहोस्': 'View',
  'सकिएन': 'Could not do that',
  'सकिएन।': 'Could not do that.',
  'प्रकाशित': 'Published',
  'मस्यौदा': 'Draft',
  'सबै': 'All',

  // — Structure and revisions —
  'रचनाको बनोट': 'Structure of the piece',
  'बनोट': 'Structure',
  'खाली': 'Empty',
  'लामो रचनालाई पृष्ठमा बाँड्न सकिन्छ — टुलबारको पृष्ठ बटन वा Ctrl+Shift+Enter।':
    'A long piece can be split into pages — the page button in the toolbar, or Ctrl+Shift+Enter.',
  'बन्द गर्नुहोस्': 'Close',
  'सम्पादन अघि': 'Before an edit',
  'प्रकाशन अघि': 'Before publishing',
  'पुनर्स्थापना अघि': 'Before a restore',
  'इतिहास ल्याउन सकिएन।': 'Could not load the history.',
  'यो संस्करण फर्काउने? हालको पाठ पनि इतिहासमा सुरक्षित हुनेछ।':
    'Restore this version? The current text will be kept in the history too.',
  'फर्काउन सकिएन।': 'Could not restore.',
  'इतिहास': 'History',
  'संस्करण इतिहास': 'Version history',
  'स्वतः सुरक्षितले पुरानो पाठ मेटाउँछ — यी त्यही पाठका प्रतिलिपि हुन्।':
    'Autosave overwrites the older text — these are copies of it.',
  'अहिलेसम्म कुनै पुरानो संस्करण छैन। लेख्न थालेपछि यहाँ देखिनेछ।':
    'No earlier versions yet. They appear here once you start writing.',
  'कुनै संस्करण छान्नुहोस्': 'Choose a version',

  // — Statistics —
  'अझै तथ्यांक छैन': 'No statistics yet',
  'अझै तथ्यांक छैन।': 'No statistics yet.',
  'तथ्यांक ल्याउन सकिएन।': 'Could not load statistics.',
  'तपाईंका रचनाहरू कति पढिए।': 'How much your pieces have been read.',
  'कुन दिन के प्रकाशित भयो': 'What was published, and when',
  'सबैभन्दा धेरै पढिएका': 'Most read',
  'यही अवधिभित्रको गणना': 'Counted within this period',
  'यो अवधिमा कुनै रचना पढिएको छैन।': 'No pieces were read in this period.',
  'पाठक': 'Readers',
  'रचना': 'Pieces',
  'मन पराइ': 'Likes',
  'दैनिक पाठक': 'Readers per day',
  '७ दिन': '7 days',
  '३० दिन': '30 days',
  '९० दिन': '90 days',

  // — Admin —
  'सिंहावलोकन': 'Overview',
  'लेखकहरू': 'Writers',
  'निमन्त्रणा': 'Invitations',
  'सम्पूर्ण मञ्चभरि': 'Across the whole platform',
  'नयाँ लेखक': 'New writers',
  'दर्ता प्रतिदिन': 'Sign-ups per day',
  'प्रकाशन': 'Publishing',
  'रचना प्रतिदिन': 'Pieces per day',
  'सबैभन्दा धेरै पढिएका रचना': 'Most-read pieces',
  'सबैभन्दा धेरै पढिएका लेखक': 'Most-read writers',
  'प्रकाशित रचना': 'Published pieces',
  'कुल पाठक': 'Total readers',
  'निलम्बित': 'Suspended',
  'आफ्नै डोमेन': 'Own domain',
  'बाँकी निमन्त्रणा': 'Invitations left',
  'दैनिक औसत पाठक': 'Average readers per day',
  'लेखक': 'Writer',
  'पर्खाइमा': 'Pending',
  'स्वीकृत': 'Accepted',
  'म्याद सकियो': 'Expired',
  'रद्द': 'Revoked',
  'निमन्त्रणा ल्याउन सकिएन।': 'Could not load invitations.',
  'निमन्त्रणा बनाउन सकिएन।': 'Could not create the invitation.',
  'रद्द गर्न सकिएन।': 'Could not revoke it.',
  'खुला निमन्त्रणा': 'Open invitation',
  'कपी भयो': 'Copied',
  'लिंक कपी': 'Copy link',
  'कसरी चिन्नुभयो?': 'How do you know them?',
  'रद्द गर्नुहोस्': 'Revoke',
  'नयाँ लेखक बोलाउनुहोस्': 'Invite a writer',
  'ह्यान्डल': 'Handle',
  'नाम': 'Name',
  'टिप्पणी': 'Note',
  'अझै कुनै निमन्त्रणा छैन।': 'No invitations yet.',
  'लेखकहरू ल्याउन सकिएन।': 'Could not load writers.',
  'आफ्नै भूमिका बदल्न मिल्दैन': 'You cannot change your own role',
  'लेखक बनाउनुहोस्': 'Make a writer',
  'एडमिन बनाउनुहोस्': 'Make an admin',
  'फिर्ता': 'Reinstate',
  'निलम्बन': 'Suspend',
  'नाम, ह्यान्डल वा इमेल': 'Name, handle or email',
  'लेखक खोज्नुहोस्': 'Find a writer',
  'साइट हेर्नुहोस्': 'View site',
  'प्रोफाइल': 'Profile',
  'अस्थायी पासवर्ड बनाउनुहोस्': 'Create a temporary password',
  'सक्रिय': 'Active',
  'यो एकपटक मात्र देखिन्छ — लेखकलाई आफैं पठाउनुहोस्, र बदल्न भन्नुहोस्।':
    'This is shown once — send it to the writer yourself, and ask them to change it.',
  'बुझें': 'Got it',
  'कोही भेटिएन।': 'Nobody found.',
  'तपाईं': 'you',
  'पासवर्ड': 'Password',
  'थिम ल्याउन सकिएन।': 'Could not load themes.',
  'बदल्न सकिएन।': 'Could not change it.',
  'मेट्न सकिएन।': 'Could not delete it.',
  'सेरिफ': 'Serif',
  'स्यान्स': 'Sans',
  'कभर सहित': 'With covers',
  'कभर बिना': 'Without covers',
  'मेट्नुहोस्': 'Delete',
  'पूर्वनिर्धारित': 'Default',
  'उपलब्ध': 'Available',
  'पूर्वनिर्धारित बनाउनुहोस्': 'Make it the default',
  'चयनित': 'Selected',
  'तपाईंको साइट पाठकलाई कस्तो देखिन्छ — रङ, अक्षर र बनोट।':
    'How your site looks to a reader — colour, type and layout.',
  'साँझको बतास': 'Evening breeze',
  'मनको ढोका खोल्दै आयो, तिम्रो नाम बोक्दै।':
    'It came opening the doors of the heart, carrying your name.',

  // — Author —
  'लेखक भेटिएन': 'Writer not found',
  'अझै कुनै प्रकाशित रचना छैन।': 'Nothing published yet.',
  'रामप्रसाद': 'Ram Prasad',
  'आफ्नै ठेगानामा आफ्नै साहित्य।': 'Your own literature, at your own address.',

  // — Domain —
  'सेट गरिएको छैन': 'Not set',
  'सुरु गर्दै': 'Starting',
  'DNS प्रमाणीकरण पर्खँदै': 'Waiting for DNS validation',
  'अद्यावधिक हुँदै': 'Updating',
  'DNS हटाइएको': 'DNS removed',
  'मेटिएको': 'Deleted',
  'रोकिएको': 'Stopped',
  'त्रुटि': 'Error',
  'डोमेन जानकारी ल्याउन सकिएन।': 'Could not load domain details.',
  'डोमेन थप्न सकिएन।': 'Could not add the domain.',
  'प्रमाणीकरण गर्न सकिएन।': 'Could not verify it.',
  'डोमेन हटाउने? तपाईंको साइट फेरि सबडोमेनमा मात्र देखिनेछ।':
    'Remove the domain? Your site will be reachable only at the subdomain again.',
  'खोल्नुहोस्': 'Open',
  'कपी गर्नुहोस्': 'Copy',
  'आफ्नै नाममा साहित्य प्रकाशित गर्नुहोस् — poetrybyram.com जस्तै।':
    'Publish under a name of your own — poetrybyram.com, say.',
  'निःशुल्क ठेगाना': 'Free address',
  'यो सधैं काम गर्छ र कहिल्यै बन्द हुँदैन।': 'This always works and is never turned off.',
  '१. यो DNS रेकर्ड थप्नुहोस्': '1. Add this DNS record',
  '२. स्वामित्व प्रमाणित गर्ने रेकर्ड': '2. The record that proves ownership',
  'DNS फैलिन केही मिनेटदेखि केही घण्टा लाग्न सक्छ। यो पृष्ठ आफैं जाँचिरहन्छ।':
    'DNS can take anywhere from a few minutes to a few hours to spread. This page keeps checking on its own.',
  'फेरि जाँच्नुहोस्': 'Check again',
  'हटाउनुहोस्': 'Remove',
  'आफ्नो डोमेन जोड्नुहोस्': 'Connect your domain',
  'तपाईंसँग पहिले नै भएको डोमेन नाम राख्नुहोस्।': 'Enter a domain name you already own.',
  'डोमेन': 'Domain',
  'आफ्नै डोमेन — चाँडै आउँदै': 'Your own domain — coming soon',
  'poetrybyram.com जस्तो आफ्नै डोमेन जोड्ने सुविधा सशुल्क योजनासँगै आउँदैछ।':
    'Connecting a domain of your own, like poetrybyram.com, is coming with the paid plan.',
  'त्यतिन्जेल तपाईंको साइट {host} मा पूर्ण रूपमा चलिरहन्छ — रचना प्रकाशन, पाठक, RSS सबै उही रहन्छ। डोमेन खुल्दा तपाईंका पुराना लिंकहरू आफैं नयाँ ठेगानामा जान्छन्।':
    'Until then your site runs in full at {host} — publishing, readers and RSS all stay exactly as they are. When domains open up, your old links will follow you to the new address on their own.',

  // — Accounts —
  'यो निमन्त्रणा जाँच्न सकिएन / Could not check this invitation': 'Could not check this invitation',
  'दर्ता गर्न सकिएन।': 'Could not create the account.',
  'निमन्त्रणा मान्य छैन': 'This invitation is not valid',
  'आफैं दर्ता गर्नुहोस्': 'Register yourself',
  'तपाईंलाई बोलाइएको छ': 'You have been invited',
  'ठेगाना': 'Address',
  'लगइन गर्न सकिएन।': 'Could not sign in.',
  'फेरि स्वागत छ': 'Welcome back',
  'आफ्नो लेखन कक्षमा फर्कनुहोस्।': 'Return to your writing room.',

  // — Time —
  'भर्खरै': 'just now',

  /*
   * Counted and composed strings.
   *
   * These are whole sentences with a `{placeholder}`, not fragments glued
   * together at the call site. Nepali puts its postposition after the noun and
   * English puts its preposition before it, so "page" + number + "मा" cannot be
   * reordered by concatenation — only by giving the translator the whole
   * sentence.
   */
  '{count} दिन': '{count} days',
  '{count} शब्द': '{count} words',
  '{count} पृष्ठ': '{count} pages',
  '{count} मिनेट': '{count} min',
  '{count} रचना': '{count} pieces',
  '{count} सक्रिय': '{count} active',
  '{count} कुल': '{count} in total',
  '{count} प्रकाशित': '{count} published',
  '{count} पाठक': '{count} readers',
  '{count} प्रकाशित रचना': '{count} published pieces',
  '{shown} / {total} रचना': '{shown} / {total} pieces',
  'अंक — {issue}': 'Issue {issue}',
  'रङ्ग: {theme}': 'Colours: {theme}',
  'पछिल्लो {days} दिनमा तपाईंका रचनाहरू कति पटक पढिए':
    'How often your pieces were read over the last {days} days',
  'स्वीकृत {date}': 'Accepted {date}',
  'म्याद {date}': 'Expires {date}',
  'अन्तिम जाँच: {at}': 'Last checked: {at}',
  '{handle} को नयाँ अस्थायी पासवर्ड': 'New temporary password for {handle}',
  '"{title}" स्थायी रूपमा मेट्ने?': 'Delete “{title}” permanently?',
  '"{name}" थिम मेट्ने? यो प्रयोग गर्ने लेखकहरू पूर्वनिर्धारित थिममा जान्छन्।':
    'Delete the “{name}” theme? Writers using it will move to the default.',
  '{handle} र तिनका सबै रचना स्थायी रूपमा मेट्ने?':
    'Permanently delete {handle} and every piece they have written?',
  'डोमेन सक्रिय छ। तपाईंका रचनाहरू अब {host} मा पढ्न सकिन्छ।':
    'The domain is live. Your pieces can now be read at {host}.',
  '{site} मा आफ्नो साहित्यिक संसार सुरु गर्नुहोस्।': 'Begin your literary world at {site}.',

  // — Remaining interface text —
  'यही फर्काउनुहोस्': 'Restore this one',
  'निमन्त्रणा बनाउनुहोस्': 'Create invitation',
  'लिंक बन्छ — तपाईंले आफैं पठाउनुपर्छ। सबै फिल्ड वैकल्पिक हुन्; भरेमा दर्ता फारम पहिल्यै भरिन्छ।':
    'This makes a link — you send it yourself. Every field is optional; anything you fill in pre-fills their sign-up form.',
  'आफ्नो डोमेन रजिस्ट्रार (GoDaddy, Namecheap, Cloudflare…) मा गएर तल दिइएको CNAME राख्नुहोस्।':
    'Go to your domain registrar (GoDaddy, Namecheap, Cloudflare…) and add the CNAME below.',
  'जोड्नुहोस्': 'Connect',
  'कलम नाम (वैकल्पिक)': 'Pen name (optional)',
  'विषय (अल्पविरामले छुट्याउनुहोस्)': 'Tags (separate with commas)',
  'कभर फोटो': 'Cover photo',
  'खाता खोल्नुहोस्': 'Create account',
  'खाता छैन?': 'No account yet?',
  'पहिले नै खाता छ?': 'Already have an account?',
  'सर्भरसँग सम्पर्क भएन': 'Could not reach the server',
  'API ले JSON फर्काएन': 'The API did not return JSON',

  // — Password recovery —
  'पासवर्ड बिर्सनुभयो?': 'Forgotten your password?',
  'इमेल राख्नुहोस् — नयाँ पासवर्ड राख्ने लिंक पठाइनेछ।':
    'Enter your email and we will send you a link to set a new password.',
  'लिंक पठाउनुहोस्': 'Send the link',
  'त्यो ठेगानामा खाता छ भने लिंक पठाइयो। इनबक्स र स्प्याम दुवै हेर्नुहोस्।':
    'If there is an account at that address, a link is on its way. Check your inbox and your spam folder.',
  'लगइनमा फर्किनुहोस्': 'Back to sign in',
  'पठाउन सकिएन।': 'Could not send it.',
  'नयाँ पासवर्ड राख्नुहोस्': 'Set a new password',
  'कम्तीमा १० अक्षरको राख्नुहोस्।': 'Use at least 10 characters.',
  'नयाँ पासवर्ड': 'New password',
  'पासवर्ड बदल्नुहोस्': 'Change password',
  'पासवर्ड बदल्न सकिएन।': 'Could not change the password.',
  'लिंक अपूर्ण छ': 'That link is incomplete',
  'इमेलको पूरा लिंक खोल्नुहोस्, वा नयाँ लिंक माग्नुहोस्।':
    'Open the full link from the email, or ask for a new one.',
  'नयाँ लिंक माग्नुहोस्': 'Ask for a new link',

  // — Address confirmation —
  'पुष्टि गर्दै…': 'Confirming…',
  'इमेल पुष्टि भयो': 'Email confirmed',
  'अब पासवर्ड बिर्सिए पनि खाता फिर्ता ल्याउन सकिन्छ।':
    'You can now recover your account if you ever forget your password.',
  'पुष्टि हुन सकेन': 'Could not confirm',
  'इमेल पुष्टि भएको छैन। पुष्टि नगरे पासवर्ड बिर्सिँदा खाता फिर्ता आउँदैन।':
    'Your email is not confirmed. Until it is, a forgotten password cannot be recovered.',
  'पुष्टि लिंक पठाउनुहोस्': 'Send the confirmation link',
  'पठाइयो — {email} को इनबक्स हेर्नुहोस्।': 'Sent — check the inbox for {email}.',

  // — Invitation delivery —
  'निमन्त्रणा पठाइयो।': 'Invitation sent.',
  'लिंक कपी भयो — तपाईंले आफैं पठाउनुहोस्।': 'Link copied — send it yourself.',
  'इमेल पठाउन सकिएन।': 'Could not send the email.',
  'फेरि पठाउनुहोस्': 'Send again',
  'पठाइयो {date}': 'Sent {date}',
  'पठाइएको छैन': 'Not sent',

  // — Profile and settings —
  'सेटिङ': 'Settings',
  'पाठकले तपाईंलाई कसरी चिन्छन्।': 'How readers know you.',
  'तस्बिर बदल्नुहोस्': 'Change photo',
  'तस्बिर राख्न सकिएन।': 'Could not save the photo.',
  'राखेमा रचनामा यही नाम देखिन्छ।': 'If set, this is the name that appears on your work.',
  'ठाउँ': 'Location',
  'वेबसाइट': 'Website',
  'सुरक्षित गर्नुहोस्': 'Save',
  'हालको पासवर्ड': 'Current password',
  'पासवर्ड बदलियो': 'Password changed',
  'नाममा {relative} मात्र राख्नुहोस् — धेरै रजिस्ट्रारले डोमेन आफैं जोड्छन्। पूरा रूप: {full}':
    'Enter only {relative} in the Name field — most registrars add your domain for you. Full form: {full}',

  // — Domain records and their live state —
  'रजिस्ट्रारमा गएर तलका रेकर्ड राख्नुहोस्। हामी हरेकलाई आफैं जाँचिरहन्छौं।':
    'Add the records below at your registrar. We check each one for you.',
  'सबै रेकर्ड भेटिए — प्रमाणपत्र जारी हुन केही मिनेट लाग्न सक्छ।':
    'All records found — the certificate can take a few more minutes to issue.',
  'साइट यहाँ देखाउन': 'To point the site here',
  'स्वामित्व प्रमाणित गर्न': 'To prove the domain is yours',
  'प्रमाणपत्र (SSL) का लागि': 'For the certificate (SSL)',
  'भेटियो': 'Found',
  'अझै भेटिएको छैन': 'Not visible yet',
  'मान मिलेन': 'Wrong value',
  'अर्कै मान छ: {found}': 'Found something else: {found}',
  'जाँच्न सकिएन': 'Could not check',
  'धेरै रजिस्ट्रारले मूल डोमेनमा CNAME राख्न दिँदैनन्।':
    'Most registrars cannot put a CNAME on a bare domain.',
  '{www} जोड्नुहोस्, अनि रजिस्ट्रारमा {apex} लाई त्यसतर्फ फर्वार्ड गर्नुहोस्।':
    'Add {www} instead, then forward {apex} to it at your registrar.',
  'www. थप्नुहोस्': 'Use www.',
  'तयार हुँदै': 'Being prepared',
  'Cloudflare ले _acme-challenge रेकर्ड बनाउँदैछ — केही क्षणमा यहीँ देखिनेछ।':
    'Cloudflare is issuing the _acme-challenge record — it will appear here in a moment.',

  // — Comments —
  'प्रतिक्रिया': 'Comments',
  '{count} प्रतिक्रिया': '{count} comments',
  'प्रतिक्रिया ल्याउन सकिएन।': 'Could not load the comments.',
  'यो रचनाबारे केही लेख्नुहोस्…': 'Say something about this piece…',
  'पठाउनुहोस्': 'Post',
  'प्रतिक्रिया लेख्न लगइन गर्नुहोस्।': 'Sign in to leave a comment.',
  'यो रचनामा प्रतिक्रिया बन्द छ।': 'Comments are closed on this piece.',
  'पहिलो प्रतिक्रिया तपाईंकै होस्।': 'Be the first to comment.',
  'सम्पादित': 'edited',
  'लुकाइएको': 'Hidden',
  'लुकाइएका': 'Hidden',
  'देखिने': 'Visible',
  'जवाफ': 'Reply',
  '{name} लाई जवाफ…': 'Reply to {name}…',
  'देखाउनुहोस्': 'Show',
  'यो प्रतिक्रिया स्थायी रूपमा मेट्ने?': 'Delete this comment permanently?',
  'पाठकहरूले तपाईंका रचनामा लेखेका कुरा।': 'What readers have written on your pieces.',
  'साइटभरि प्रतिक्रिया खुला': 'Comments open across the site',
  'बन्द गर्दा कुनै पनि रचनामा नयाँ प्रतिक्रिया आउँदैन। पुराना मेटिँदैनन्।':
    'Turning this off stops new comments everywhere. Existing ones are kept.',
  'अहिलेसम्म कुनै प्रतिक्रिया छैन।': 'No comments yet.',
  'प्रतिक्रिया खुला': 'Comments open',
  'साइटभरि प्रतिक्रिया बन्द छ, त्यसैले यो असर गर्दैन।':
    'Comments are closed site-wide, so this has no effect.',

  // — Theme customiser —
  'रङ': 'Colour',
  'ढाँचा': 'Layout',
  'मूल अक्षर': 'Body type',
  'शीर्षकको अक्षर': 'Heading type',
  'खुलापन': 'Spacing',
  'कुना': 'Corners',
  'कागज': 'Paper',
  'श्लोक चिन्ह': 'Stanza mark',
  'कभर फोटो देखाउनुहोस्': 'Show cover photos',
  'बन्द गर्दा पनि फोटो सेयर कार्डमा रहन्छ।': 'The photo still appears on share cards.',
  'आफ्नै अनुसार': 'Make it yours',
  'थिम रोज्दा तपाईंले आफैं गरेका परिवर्तन हट्छन्।':
    'Choosing a theme clears the changes you made yourself.',
  'तपाईंले यो थिमका केही कुरा आफैं बदल्नुभएको छ।': 'You have changed parts of this theme yourself.',
  'रोजेको थिममाथि आफ्नै रङ, बनोट र अक्षर छान्न सकिन्छ।':
    'Pick your own colour, layout and type on top of the theme you chose.',
  'थिम जस्तो थियो, त्यस्तै बनाउनुहोस्': 'Put the theme back as it was',
  'पहिलेकै बनाउनुहोस्': 'Reset to the theme',

  // — The public typing tool —
  'नेपाली टाइपिङ': 'Nepali typing',
  'नेपाली टाइपिङ — रोमनबाट देवनागरी | कलम प्रेस': 'Nepali Typing — roman to Devanagari | Kalam Press',
  'अंग्रेजी अक्षरमा लेख्नुहोस्, स्पेस थिच्नासाथ देवनागरीमा बदलिन्छ। खाता चाहिँदैन।':
    'Type in roman letters; press space and it becomes Devanagari. No account needed.',
  'yaha lekhna suru garnuhos… (यहाँ लेख्न सुरु गर्नुहोस्…)':
    'yaha lekhna suru garnuhos… (यहाँ लेख्न सुरु गर्नुहोस्…)',
  '{words} शब्द · {characters} अक्षर': '{words} words · {characters} characters',
  'शब्द अर्कै आयो? Ctrl+Space थिच्नुहोस् — अरू रूपहरू देखिन्छन्। तपाईंले रोजेको सम्झिन्छ।':
    'Wrong word? Press Ctrl+Space for the other readings. Your choice is remembered.',
  'कसरी लेख्ने': 'How to type',
  'रोमनमा नलेखिने चिन्हहरू — क्लिक गरेर कपी गर्नुहोस्।':
    'Marks with no roman spelling — click to insert one.',
  'लेख्न मन छ?': 'Want to publish?',
  'कलम प्रेसमा कविता, कथा र निबन्ध प्रकाशित गर्नुहोस् — आफ्नै ठेगानामा, निःशुल्क।':
    'Publish your poems, stories and essays on Kalam Press — at your own address, free.',
  'निःशुल्क': 'Free',

  // — The tool's privacy page —
  //
  // Translated as carefully as the Nepali is written. Someone reaching this page
  // in English is doing so because they want to know what a script on a page
  // they do not control is doing with their keystrokes, and a vague translation
  // of a precise sentence is worse than no page.
  'गोपनीयता': 'Privacy',
  'गोपनीयता — नेपाली टाइपिङ': 'Privacy — Nepali Typing',
  'तपाईंले टाइप गरेको कुरा तपाईंकै ब्राउजरमा रहन्छ।': 'What you type stays in your browser.',
  'पूरा विवरण — के पठाइन्छ, के राखिन्छ, कसरी बन्द गर्ने':
    'The full detail — what is sent, what is kept, how to turn it off',

  'टाइपिङ कहाँ हुन्छ': 'Where the typing happens',
  'रोमनबाट देवनागरी बनाउने काम पूरै तपाईंको ब्राउजरभित्र हुन्छ। तपाईंले लेखेको वाक्य कुनै सर्भरसम्म पुग्दैन — इन्टरनेट काटिए पनि टाइपिङ उस्तै चल्छ।':
    'Turning Roman letters into Devanagari happens entirely inside your browser. The sentences you write never reach a server — cut the internet and the typing still works.',

  // 'के पठाइन्छ' is translated with the docs below — the same heading, and the
  // two pages must not drift apart on it.
  'एउटै कुरा। अनुमान गलत भएर तपाईंले अर्को रूप रोज्नुभयो भने, त्यो जोडी पठाइन्छ — रोमन शब्द, र तपाईंले रोजेको देवनागरी शब्द।':
    'One thing. When the guess is wrong and you reach for a different reading, that pair is sent — the Roman word, and the Devanagari word you chose.',
  'यति नै। वाक्य होइन, वरिपरिको पाठ होइन, तपाईं को हो भन्ने होइन, र कुन पृष्ठबाट आयो भन्ने पनि होइन — अनुरोध no-referrer सँग जान्छ, त्यसैले ठेगाना पनि पठाइँदैन।':
    'That is all of it. Not the sentence, not the surrounding text, not who you are, and not which page it came from — the request is sent with no-referrer, so the address does not travel either.',
  'किन: pani भनेको पानी पनि हो, पनि पनि हो। कुन बढी चलनचल्तीमा छ भन्ने कुरा अनुमान गरेर होइन, गनेर मात्र थाहा हुन्छ। यही कारण यो मस्यौदामा पनि चल्छ — शब्द रोजाइको सूची कसैको कविताको प्रतिलिपि होइन।':
    'Why: pani is both पानी and पनि. Which reading is the common one is not something judgement settles — only counting does. It is also why this can run on a draft: a table of word choices is not a copy of anyone’s poem.',

  'कहाँ कहिल्यै लाग्दैन': 'Where it never runs',
  'पासवर्ड फिल्डमा — जे लेखे पनि, कहिल्यै होइन।': 'Password fields — whatever is marked on them, never.',
  'इमेल, URL, फोन, नम्बर, मिति, समय, फाइल र लुकेका फिल्डमा।':
    'Email, URL, telephone, number, date, time, file and hidden fields.',
  'ब्राउजरले Global Privacy Control वा Do Not Track पठाएको छ भने कतै पनि होइन।':
    'Anywhere at all, if your browser sends Global Privacy Control or Do Not Track.',

  'बन्द गर्ने तरिका': 'Turning it off',
  'यही औजारमा — टाइपिङ बाकसमुनिको बटनबाट।': 'On this tool — the switch under the typing box.',
  'गृह पृष्ठ': 'Home',
  'कलम प्रेसमा लेख्नेहरूलाई — प्रोफाइल सेटिङमा।': 'If you write on Kalam Press — in your profile settings.',
  'आफ्नो साइटमा राख्नेहरूलाई — data-contribute="off", एउटा फिल्डमा वा पूरै स्क्रिप्टमा।':
    'If you host it on your own site — data-contribute="off", on one field or on the whole script.',
  'बन्द गर्दा टाइपिङमा कुनै फरक पर्दैन — सबै सुविधा उस्तै रहन्छन्। लेख्नेको छनोट सर्भरले पनि मान्छ, त्यसैले यो ब्राउजरले ठीकसँग काम गरेकोमा भर पर्दैन।':
    'Turning it off changes nothing about the typing — every feature stays. The server honours a writer’s choice as well, so it does not depend on this browser behaving.',

  'के भण्डारण हुन्छ': 'What is kept',
  'शब्दजोडी, र त्यसलाई कति जनाले रोजे भन्ने गन्ती।': 'The word pair, and a count of how many people chose it.',
  'भोट कसको हो भन्ने चिनारी — तपाईंको IP ठेगानाबाट बनेको गोप्य ह्यास। ठेगाना आफैँ कहीँ लेखिँदैन र ह्यासबाट फर्काउन सकिँदैन।':
    'Who a vote belongs to — a keyed hash derived from your IP address. The address itself is never written down, and the hash cannot be turned back into one.',
  'चिनारी किन चाहियो: भोट गन्न सकिनुपर्छ। नत्र एक जनाले पाँच पटक पठाएर सबैले पाउने शब्द बदल्न सक्थे। एउटै कार्यालय वा क्याम्पसभित्रका सबै जना एक जना गनिन सक्छन् — यो जानी–जानी छानिएको पक्ष हो, किनभने कम गन्दा सुधार ढिलो हुन्छ, बढी गन्दा एक जनाले सबैको हिज्जे तय गर्छ।':
    'Why an identity is needed at all: votes have to be countable. Otherwise one person sending five could change the word everybody gets. Everyone in one office or one campus may count as a single person — a trade-off chosen deliberately, because under-counting only delays a correction while over-counting lets one person settle a spelling for everyone.',

  'खाता र API कुञ्जी': 'Accounts and API keys',
  'खाता कुञ्जी बनाउन मात्र चाहिन्छ। टाइपिङ, एम्बेड र यो पृष्ठका लागि चाहिँदैन।':
    'An account is needed only to create a key. Not for typing, not for the embed, not for this page.',
  'खातामा: इमेल र पासवर्डको ह्यास।': 'On the account: an email address and a password hash.',
  'कुञ्जीको ह्यास मात्र राखिन्छ। कुञ्जी आफैँ एक पटक देखाइन्छ — त्यसपछि हामीसँग पनि हुँदैन।':
    'Only a hash of the key is stored. The key itself is shown once — after that we do not have it either.',
  'दिनको अनुरोध र शब्द गन्ती — बिल गर्न होइन, सीमा हेर्न।':
    'A per-day count of requests and words — for the limit, not for billing.',
  'API मा पठाइएको पाठ कतै राखिँदैन। बदलिन्छ, फर्काइन्छ, र बिर्सिन्छ।':
    'Text sent to the API is not stored anywhere. It is converted, returned, and forgotten.',

  'कुकी र ट्र्याकिङ': 'Cookies and tracking',
  'साइन इन गर्नुभयो भने एउटा कुकी — सत्र चलाउन। त्यति नै।':
    'One cookie if you sign in, to keep the session. That is the only one.',
  'कुनै एनालिटिक्स छैन। कुनै विज्ञापन छैन। कुनै तेस्रो पक्षको ट्र्याकर छैन।':
    'No analytics. No advertising. No third-party trackers.',

  'हटाउन वा सोध्न': 'Removal, or a question',
  'शब्दजोडीहरू कसैको नाममा जोडिएका छैनन्, त्यसैले आफ्नो भनेर छुट्याउन मिल्दैन — यो त्यही सीमितताको अर्को नाम हो जसले यसलाई बेनामी बनाउँछ। खाता वा कुञ्जी हटाउन, वा कुनै प्रश्न भए:':
    'Word pairs are not attached to anyone, so there is no set of them that is yours to single out — which is the same fact as their being anonymous, seen from the other side. To delete an account or a key, or to ask anything:',
  'अन्तिम अद्यावधिक': 'Last updated',
  'यही नियम कलम प्रेसमा पनि लागू हुन्छ।': 'The same policy applies on Kalam Press.',

  // — The tool's own site and its integration docs —
  'यो फिल्डमुनि सानो बटनपट्टी नदेखाउनुहोस्।': 'Do not show the small control bar under this field.',
  'पूरै पृष्ठमा बटनपट्टी नदेखाउनुहोस्।': 'Do not show the control bar anywhere on the page.',
  'हरेक फिल्डमुनि सानो बटनपट्टी देखिन्छ — रोमनबाट बदल्ने, अरू रूप हेर्ने, ॐ जस्ता चिन्ह हाल्ने र देवनागरी/ASCII फेर्ने। नचाहिए data-toolbar="off"।':
    'A small control bar appears under each field — convert from Roman, see other readings, insert marks like ॐ, and switch between Devanagari and ASCII. Turn it off with data-toolbar="off".',
  'जोड्ने तरिका': 'Add to your site',
  'जोड्ने तरिका — नेपाली टाइपिङ': 'Add to your site — Nepali Typing',
  'निःशुल्क, खुला — नेपाली भाषाका लागि।': 'Free and open, for the Nepali language.',
  'आफ्नो वेबसाइटमा जोड्नुहोस्': 'Add it to your website',
  'एक लाइन HTML। खाता चाहिँदैन, शुल्क छैन।': 'One line of HTML. No account, no charge.',
  'सुरु गर्नुहोस्': 'Get started',
  '`data-nepali` भएका फिल्डमा मात्र लाग्छ। अरू केही छुँदैन।':
    'It touches only fields marked `data-nepali`. Nothing else on your page.',
  'पछि थपिने फिल्डहरू': 'Fields added later',
  'मोडल, कमेन्ट बाकस, React — पछि देखिने फिल्ड आफैं समातिन्छ। केही गर्नु पर्दैन।':
    'Modals, comment boxes, React — fields that appear later are picked up on their own. Nothing to call.',
  'कोडबाट': 'From code',
  'के पठाइन्छ': 'What gets sent',
  'कसैले Ctrl+Space बाट शब्द सच्याए भने त्यो एक शब्द मात्र पठाइन्छ — वाक्य होइन, फारमको अरू कुरा होइन, को हो भन्ने होइन। यसैले सबैका लागि अनुमान सुधारिन्छ।':
    'When somebody corrects a word with Ctrl+Space, that one word is sent — not the sentence, not the rest of the form, not who typed it. It is what improves the guess for everyone.',
  'बन्द गर्न:': 'To turn it off:',
  'पासवर्ड फिल्डमा कहिल्यै लाग्दैन — जे लेखे पनि।':
    'Never attaches to a password field, whatever the markup says.',
  'इमेल, URL, नम्बर, मिति फिल्डमा पनि लाग्दैन।': 'Nor to email, URL, number or date fields.',
  'ब्राउजरले Global Privacy Control वा DNT पठाएको छ भने केही पठाइँदैन।':
    'Sends nothing at all when the browser signals Global Privacy Control or DNT.',
  'आकार': 'Size',
  '७.६ KB (gzip)। कुनै निर्भरता छैन, कुनै अनुरोध छैन — टाइप गर्दा सर्भरसँग कुरा हुँदैन।':
    '7.6 KB gzipped. No dependencies, and no request while typing — conversion happens in the browser.',
  'साहित्य प्रकाशित गर्न चाहनुहुन्छ?': 'Want to publish your writing?',
  'टाइपिङ बाकसमा फर्किनुहोस्': 'Back to the typing box',

  // — The tool's landing page —
  'नेपाली टाइपिङ — रोमनबाट देवनागरी': 'Nepali Typing — roman to Devanagari',
  'रोमनमा लेख्नुहोस्, नेपालीमा पाउनुहोस्': 'Type in roman, get Nepali',
  'nepal लेख्नुहोस्, नेपाल बन्छ। स्पेस थिच्नासाथ। खाता चाहिँदैन, केही डाउनलोड गर्नु पर्दैन।':
    'Type nepal, get नेपाल — as soon as you press space. No account, nothing to install.',
  'आफ्नै वेबसाइटमा राख्नुहोस्': 'Put it on your own site',
  'एक लाइन। तपाईंका पाठकले कमेन्ट, फारम, जहाँ पनि नेपालीमा लेख्न पाउँछन्।':
    'One line. Your readers can write Nepali in comments, forms, anywhere.',
  '७.६ KB, शून्य अनुरोध': '7.6 KB, zero requests',
  'टाइप गर्दा सर्भरसँग कुरा हुँदैन — सबै ब्राउजरमै हुन्छ। त्यसैले छिटो छ, र त्यसैले निःशुल्क छ।':
    'Nothing talks to a server while you type — it all happens in the browser. That is why it is fast, and why it is free.',
  'पासवर्ड कहिल्यै छुँदैन': 'Never touches a password',
  'data-nepali लेखिएका फिल्डमा मात्र लाग्छ। पासवर्ड, इमेल, नम्बरमा कहिल्यै लाग्दैन।':
    'It attaches only to fields marked data-nepali — never to password, email or number fields.',
  'सिक्दै जान्छ': 'It learns',
  'कसैले शब्द सच्याए भने त्यो सम्झिन्छ — र धेरैले उही सच्याए भने सबैका लागि सुध्रिन्छ।':
    'A correction is remembered — and when enough people make the same one, the guess improves for everybody.',
  'साहित्यका लागि बनेको': 'Built for literature',
  'गजल, मुक्तक, कविता प्रकाशित गर्ने मञ्चबाट आएको — नेपाली लेख्ने मान्छेले नै बनाएको।':
    'From a platform that publishes ghazals, muktaks and poems — made by people who write Nepali.',

  // — Accounts on the tool —
  'खाता बनाउनुहोस्': 'Create an account',
  'कम्तीमा १० अक्षर।': 'At least 10 characters.',
  'पहिले नै खाता छ? लगइन गर्नुहोस्': 'Already have an account? Sign in',
  'खाता छैन? बनाउनुहोस्': 'No account? Create one',
  'कुञ्जी बनाउन खाता चाहिन्छ। टाइपिङका लागि भने केही चाहिँदैन।':
    'A key needs an account. Typing itself needs nothing.',

  // — Docs reference —
  'एट्रिब्युट': 'Attributes',
  'संस्करण': 'Versioning',
  'यो फिल्डमा नेपाली टाइपिङ चलाउनुहोस्।': 'Turn on Nepali typing for this field.',
  'यो फिल्डबाट सच्याइ नपठाउनुहोस्।': 'Send no corrections from this field.',
  'पूरै पृष्ठबाट सच्याइ नपठाउनुहोस्।': 'Send no corrections from the whole page.',
  'साझा सिकाइका लागि आफ्नो कुञ्जी जोड्नुहोस् (वैकल्पिक)।':
    'Attach your key to the shared ranking (optional).',
  'साझा सिकाइ नपुगे पनि टाइपिङ चल्छ — यो कहिल्यै रोकावट बन्दैन।':
    'Typing works even when the shared ranking is unreachable — it is never a dependency.',
  'पूरा पाठ बदल्नुहोस्।': 'Convert a whole piece of text.',
  'एउटा शब्दका अरू रूपहरू।': 'The other readings of one word.',
  'आज कति खर्च भयो।': 'What this key has spent today.',
  'त्रुटिहरू': 'Errors',
  'कुञ्जी छैन, गलत छ, वा खारेज भइसक्यो।': 'Missing, wrong, or revoked key.',
  'आजको सीमा पुग्यो। ००:०० UTC मा फेरि सुरु हुन्छ।': 'Daily limit reached. Resets at 00:00 UTC.',
  'पाठ खाली छ वा धेरै लामो छ (२०,००० अक्षरभन्दा बढी)।':
    'The text is empty or too long (over 20,000 characters).',
  'हरेक त्रुटिमा `error` फिल्ड हुन्छ, र तपाईंको Accept-Language अनुसार नेपाली वा अंग्रेजीमा आउँछ।':
    'Every error carries an `error` field, in Nepali or English according to your Accept-Language.',
  'संस्करण बाटोमै छ — /v1/type.js। सुधार यही ठेगानामा आउँछ; फरक पर्ने परिवर्तन भए /v2 बन्छ, र /v1 चल्दै रहन्छ।':
    'The version is in the path — /v1/type.js. Fixes ship to that address; a breaking change becomes /v2, and /v1 keeps working.',

  // — The API and its keys —
  'सर्भरबाट (API)': 'From a server (API)',
  'ब्राउजर नभएको ठाउँका लागि — भण्डारमा भएको पुरानो लेख बदल्न, वा मोबाइल एपको सर्भरबाट। टाइपिङका लागि यो चाहिँदैन।':
    'For where there is no browser — converting a stored archive, or from a mobile app’s backend. Typing does not need this.',
  'शब्दमा गनिन्छ, अनुरोधमा होइन — एउटा लेख र एउटा नाम उही मेहनत होइनन्। दिनको ५०,००० शब्द निःशुल्क।':
    'Metered in words, not requests — an article and a name are not the same work. 50,000 words a day, free.',
  'अरू: POST /api/v1/candidates र GET /api/v1/usage':
    'Also: POST /api/v1/candidates and GET /api/v1/usage',
  'निःशुल्क कुञ्जी लिनुहोस्': 'Get a free key',
  // Sample output inside a code block — content, not interface copy.
  'मेरो देश नेपाल': 'मेरो देश नेपाल',
  'API कुञ्जी': 'API keys',
  'API कुञ्जी — नेपाली टाइपिङ': 'API keys — Nepali Typing',
  'टाइपिङका लागि कुञ्जी चाहिँदैन — त्यो ब्राउजरमै हुन्छ। यो साझा सिकाइ र सर्भरबाट अनुवाद गर्ने API का लागि हो।':
    'Typing needs no key — that happens in the browser. Keys are for the shared ranking and the server-side API.',
  'यो कुञ्जी अहिले मात्र देखिन्छ — कतै सुरक्षित राख्नुहोस्।':
    'This key is shown once — store it somewhere safe.',
  'नयाँ कुञ्जी': 'New key',
  'मेरो साइट': 'My site',
  'अहिलेसम्म कुनै कुञ्जी छैन।': 'No keys yet.',
  'अन्तिम प्रयोग {when}': 'Last used {when}',
  'अझै प्रयोग भएको छैन': 'Never used',
  'आज {words} / {limit} शब्द': '{words} / {limit} words today',
  'सीमा पुग्यो': 'Limit reached',
  'खारेज गर्नुहोस्': 'Revoke',
  'यो कुञ्जी खारेज गर्ने? यसले चल्न छोड्छ।': 'Revoke this key? It will stop working.',
  'कसरी प्रयोग गर्ने भन्ने': 'How to use it:',
  'कुञ्जी बनाउन लगइन गर्नुहोस्। टाइपिङका लागि भने केही चाहिँदैन।':
    'Sign in to create a key. Typing itself needs nothing.',
  'लगइन भएन।': 'Could not sign in.',
  'बन्न सकेन।': 'Could not create it.',

  // — Contributing to the shared ranking —
  'नेपाली टाइपिङ सुधार्न सघाउनुहोस्': 'Help improve Nepali typing',
  'टाइपिङ सुधार्न सघाउनुहोस्': 'Help improve typing',
  'तपाईंले सच्याएको शब्द मात्र पठाइन्छ — वाक्य होइन, नाम होइन। यसले सबैका लागि अनुमान सुधार्छ।':
    'Only the word you corrected is sent — not the sentence, not your name. It improves the guess for everyone.',
  'तपाईंले Ctrl+Space बाट सच्याएको शब्द मात्र पठाइन्छ — वाक्य होइन, खाता होइन। यसले सबैका लागि अनुमान सुधार्छ।':
    'Only the words you correct with Ctrl+Space are sent — not the sentence, not your account. They improve the guess for everyone.',

  // — Admin: the shared ranking —
  'टाइपिङ सिकाइ': 'Typing',
  'मानिसहरूले सच्याएका शब्दहरू। {votes} जनाभन्दा बढीले रोजेपछि त्यही पूर्वनिर्धारित बन्छ।':
    'Words people have corrected. Once more than {votes} of them agree, it becomes the default.',
  'रोमन शब्द खोज्नुहोस्': 'Search a romanized word',
  'खोज्नुहोस्': 'Search',
  'अहिलेसम्म कुनै सच्याइ आएको छैन।': 'No corrections yet.',
  'ल्याउन सकिएन।': 'Could not load that.',
  '{count} रूप': '{count} readings',
  '{count} जना': '{count} people',
  'इन्जिन: {text}': 'engine: {text}',
  'लागू': 'In use',
  'सूचीमा मात्र': 'List only',
  'रोक्नुहोस्': 'Block',
  'अस्वीकृत': 'Blocked',
  'स्वीकृत गर्नुहोस्': 'Unblock',

  // — Theme administration —
  'नयाँ थिम': 'New theme',
  'थिम बदल्नुहोस्': 'Edit theme',
  'यी नै टोकनहरू लेखकको साइटमा लाग्छन् — तल देखिएको नै पाठकले देख्ने हो।':
    'These are the tokens a writer’s site is rendered with — the preview is what a reader sees.',
  'कुञ्जी': 'Key',
  'नाम (अंग्रेजी)': 'Name (English)',
  'नाम (नेपाली)': 'Name (Nepali)',
};
/* end EN */
